import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentMultiAccountTransferData,
  type EnrollmentMultiAccountTransferSubmitInput,
  type EnrollmentMultiAccountTransferSubmitOutput,
  type EnrollmentMultiAccountTransferAccountData,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment Multi AccountTransfer Account data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiAccountTransferData>
 * @example const response = await getEnrollmentMultiAccountTransferAccountData()
 */
export const getEnrollmentMultiAccountTransferAccountData = async (
  context?: DefaultContext,
): Promise<EnrollmentMultiAccountTransferAccountData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentMultiAccountTransferData',
      query: `query getEnrollmentMultiAccountTransferData {
        wealthOverview {
          personalPortfolio {
            accounts {
              ... on InvestmentBrokerageCompanionAccount {
                alias
                attributes {
                  acatAllowed
                }
              }
            }
          }
        }
      }`,
    },
    {},
    context,
  )) as { wealthOverview: EnrollmentMultiAccountTransferAccountData }
  return response.wealthOverview
}

/**
 * Get Enrollment Multi AccountTransfer data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiAccountTransferData>
 * @example const response = await getEnrollmentMultiAccountTransferData()
 */
export const getEnrollmentMultiAccountTransferData = async (
  context?: DefaultContext,
): Promise<EnrollmentMultiAccountTransferData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentMultiAccountTransferData',
      query: `mutation getEnrollmentMultiAccountTransferData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              applicationId
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiAccountTransferData }
  return response.submitEnrollment
}

export const useGetEnrollmentMultiAccountTransferData = createQueryHook<undefined, EnrollmentMultiAccountTransferData>(
  getEnrollmentMultiAccountTransferData,
)

/**
 * Submit Enrollment Multi AccountTransfer data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentMultiAccountTransferSubmitOutput>
 * @example const response = await submitEnrollmentMultiAccountTransferData();
 */
export const submitEnrollmentMultiAccountTransferData = async (
  enrollmentAccountTransferSubmitInput: EnrollmentMultiAccountTransferSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentMultiAccountTransferSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentMultiAccountTransferData',
      query: `mutation submitEnrollmentMultiAccountTransferData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentAccountTransferSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentMultiAccountTransferSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentMultiAccountTransferData = createMutationHook<
  EnrollmentMultiAccountTransferSubmitInput,
  EnrollmentMultiAccountTransferSubmitOutput
>(submitEnrollmentMultiAccountTransferData)

export const useGetEnrollmentMultiAccountTransferAccountData = createQueryHook<
  undefined,
  EnrollmentMultiAccountTransferAccountData
>(getEnrollmentMultiAccountTransferAccountData)
